/**
 * One comparator for the comparison table and the product cards, so a page
 * rendering both cannot show two orders. Mirrors `sortProductsByApprovalOdds`.
 */

export type ProductSortDirection = 'asc' | 'desc';

/** Distinct from a real `0`, which is a value that must rank normally. */
export type MeasureValue = number | null | undefined;

/**
 * Evaluated per call, not once per product: a calculated measure can have a
 * figure at one loan amount and tenure and none at another.
 */
const hasValue = (value: MeasureValue): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Lowest first, and a missing value after one that reads. Not scaled by the
 * measure's direction: a tie-break is a secondary figure such as a price, which
 * reads cheapest first however the measure itself is ranked.
 */
const compareTieBreak = (a: MeasureValue, b: MeasureValue): number => {
  if (hasValue(a) && hasValue(b)) return a - b;
  if (hasValue(a) !== hasValue(b)) return hasValue(a) ? -1 : 1;
  return 0;
};

/**
 * Non-mutating, and stable through an explicit index tie-breaker rather than
 * the engine's own sort stability. Missing values rank last — see `hasValue`.
 *
 * @param getValue  `null`/`undefined` when the product has no value.
 * @param getTieBreak  Orders products whose values are equal, lowest first,
 *   before the input order does. Omitted, equal values keep the input order.
 */
export function sortProductsByMeasure<T>(
  products: readonly T[],
  getValue: (product: T) => MeasureValue,
  direction: ProductSortDirection = 'asc',
  getTieBreak?: (product: T) => MeasureValue,
): T[] {
  const sign = direction === 'desc' ? -1 : 1;

  return products
    .map((product, index) => ({
      product,
      index,
      value: getValue(product),
      tieBreak: getTieBreak?.(product),
    }))
    .sort((a, b) => {
      const aHas = hasValue(a.value);
      const bHas = hasValue(b.value);

      // To the end regardless of direction: "no data" is not "worst value".
      if (!aHas || !bHas) {
        if (aHas === bHas) return a.index - b.index;
        return aHas ? -1 : 1;
      }

      const diff = (a.value as number) - (b.value as number);
      if (diff !== 0) return diff * sign;

      return compareTieBreak(a.tieBreak, b.tieBreak) || a.index - b.index;
    })
    .map(({ product }) => product);
}
